import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  AvatarAtomComponent,
  ButtonIconAtomComponent,
} from '@ls-commons/components';
import { getRoleLabel, UserAuth } from '@ls-commons/models';
import { UserService } from '@ls-commons/users/services';
import { errorToast, successToast } from '@ls-commons/utils/toasts';
import { MessageService } from 'primeng/api';
import { InputText } from 'primeng/inputtext';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ToggleSwitch, ToggleSwitchChangeEvent } from 'primeng/toggleswitch';

@Component({
  selector: 'lib-user-installation-assign',
  standalone: true,
  imports: [
    CommonModule,
    InputText,
    FormsModule,
    ToggleSwitch,
    ReactiveFormsModule,
    AvatarAtomComponent,
    ButtonIconAtomComponent,
  ],
  templateUrl: './user-installation-assign.component.html',
  styleUrl: './user-installation-assign.component.scss',
})
export class UserInstallationAssignComponent {
  readonly getRoleLabel = getRoleLabel;

  installationsData: any[] = [];
  searchInstallation = '';
  formInstallationAssigned: FormGroup;
  userId: number;
  assignedInstallationCount = 0;
  userData: UserAuth;

  constructor(
    public ref: DynamicDialogRef,
    private fb: FormBuilder,
    private userService: UserService,
    private messageService: MessageService,
    public config: DynamicDialogConfig
  ) {
    this.formInstallationAssigned = this.fb.group({
      installations: this.fb.array([]),
    });

    const { installationsData, userId, userData } = this.config.data;

    this.userData = userData;
    this.userId = userId;
    this.installationsData = this.sortInstallations(installationsData);
    this.getChangesForm();
    this.pushInstallations();
  }

  /** Autorizadas primero, luego orden alfabético por nombre. */
  private sortInstallations(installations: any[]): any[] {
    return [...installations].sort((a, b) => {
      const aAssigned = a?.assigned?.usuario_id === this.userId;
      const bAssigned = b?.assigned?.usuario_id === this.userId;
      if (aAssigned !== bAssigned) return aAssigned ? -1 : 1;
      return a.name.localeCompare(b.name, 'es', { sensitivity: 'base' });
    });
  }

  pushInstallations() {
    this.installationsData.forEach(installation => {
      const group = this.fb.group({
        installation_id: [installation.id],
        data: [installation],
        register_id: [installation?.assigned?.id],
        user_old_id: [installation?.assigned?.user_id],
        checked: [
          [
            // Agrega 1 si tiene email, 2 si tiene SMS, 3 si tiene PUSH en un solo array
            ...(installation?.assigned?.notify_email === 1 ? [1] : []),
            ...(installation?.assigned?.notify_sms === 1 ? [2] : []),
            ...(installation?.assigned?.notify_push === 1 ? [3] : []),
          ],
        ],

        isAssigned: [installation?.assigned?.usuario_id === this.userId],
      });

      this.installations.push(group);
    });
  }

  getChangesForm() {
    this.formInstallationAssigned.valueChanges.subscribe(x => {
      this.assignedInstallationCounter();
    });
  }

  get installations(): FormArray {
    return this.formInstallationAssigned.get('installations') as FormArray;
  }

  installationTrackBy(index: number, control: any) {
    return control.get('installation_id')?.value;
  }

  matchesSearch(control: AbstractControl) {
    const search = this.searchInstallation.trim().toLowerCase();
    if (!search) return true;

    const data = control.get('data')?.value;
    return (
      data.id.toString().includes(search) ||
      data.name.toLowerCase().includes(search) ||
      (data.empresa ?? '').toLowerCase().includes(search) ||
      (data.direccion ?? '').toLowerCase().includes(search)
    );
  }

  get fullName(): string {
    const nombre = (this.userData as any)?.nombre ?? '';
    const apellidos =
      (this.userData as any)?.apellidos ?? this.userData?.surname ?? '';
    return `${nombre} ${apellidos}`.trim();
  }

  initials(): string {
    const nombre = (this.userData as any)?.nombre ?? '';
    const apellidos =
      (this.userData as any)?.apellidos ?? this.userData?.surname ?? '';
    return `${nombre[0] ?? ''}${apellidos[0] ?? ''}`.toUpperCase();
  }

  isChannelActive(control: AbstractControl, channel: 1 | 2): boolean {
    return !!control.get('checked')?.value?.includes(channel);
  }

  toggleChannel(control: AbstractControl, channel: 1 | 2): void {
    if (!control.get('isAssigned')?.value) return;

    const current: number[] = control.get('checked')?.value ?? [];
    const next = current.includes(channel)
      ? current.filter(value => value !== channel)
      : [...current, channel];

    control.get('checked')?.setValue(next);
    this.sendRecord(control);
  }

  close(): void {
    this.ref.close(true);
  }

  sendRecord(control: AbstractControl<any, any>) {
    const record = this.haddleRecordData(control);

    this.userService.updateInstallationsUsers(record).subscribe({
      next: (res: any) => {
        this.succesToast();
      },
      error: (err: any) => {
        this.errorToast();
      },
    });
  }

  toggleAssigned(
    $event: ToggleSwitchChangeEvent,
    control: AbstractControl<any, any>
  ) {
    if ($event.checked) {
      this.createInstallationAssignUser(control);
    } else {
      this.deleteInstallationAssignUser(control);
    }
  }

  deleteInstallationAssignUser(control: AbstractControl<any, any>) {
    this.userService
      .deleteInstallationUser(control.value.register_id)
      .subscribe({
        next: (res: any) => {
          control.patchValue({
            checked: [], // quita MAIL, SMS y PUSH
            register_id: null,
            user_old_id: null,
            isAssigned: false,
          });
          this.succesToast();
        },
        error: (err: any) => {
          this.errorToast();
        },
      });
  }

  haddleRecordData(control: AbstractControl<any, any>) {
    return {
      id: control.value.register_id,
      user_id: null,
      installation_id: control.value.installation_id,
      notify_email: control.value.checked?.includes(1) ? 1 : 0,
      notify_sms: control.value.checked?.includes(2) ? 1 : 0,
      notify_push: control.value.checked?.includes(3) ? 1 : 0,
      usuario_id: control.value.isAssigned ? this.userId : null,
    };
  }

  createInstallationAssignUser(control: AbstractControl<any, any>) {
    const record = this.haddleRecordData(control);

    this.userService.createInstallationUser(record).subscribe({
      next: (res: any) => {
        control.patchValue({
          register_id: res.id, // asigna el nuevo ID
          user_old_id: this.userId,
          isAssigned: true,
        });
        this.succesToast();
      },
      error: (err: any) => {
        this.errorToast();
      },
    });
  }

  succesToast() {
    successToast('Acción realizada con exito', this.messageService, 'users');
  }
  errorToast() {
    errorToast(
      'No se ha podido realizar la acción',
      this.messageService,
      'users'
    );
  }

  assignedInstallationCounter() {
    let num = 0;
    this.formInstallationAssigned.value.installations.map((control: any) => {
      if (control.isAssigned) {
        num++;
      }
    });

    this.assignedInstallationCount = num;
  }
}
